/**
 * King Living Override - TeaserTileBlock Hook
 *
 * Override to use 'full' variant by default to include nested image data
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseTeaserTileBlockById,
  getFullTeaserTileBlockById,
  getTeaserTileBlockById,
} from '@amblique/contentful/app/contentful/queries';

/**
 * Hook for fetching a TeaserTileBlock by its ID
 * King Living override: defaults to 'full' variant to include image data
 *
 * @param {string} id - The Contentful entry ID
 * @param {Object} [options={}] - Hook options
 * @param {'base'|'default'|'full'} [options.variant='full'] - Query variant
 * @param {Object} [options.variables={}] - GraphQL query variables
 * @param {Object} [options.queryOptions={}] - React Query options
 * @param {RequestInit} [options.requestOptions={}] - HTTP request options
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.TeaserTileBlock|null, Error>}
 */
export function useTeaserTileBlockById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full', // King Living: default to 'full' instead of 'default'
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  // Select query based on variant
  const query = variant === 'base' ?
    getBaseTeaserTileBlockById :
    variant === 'full' ?
      getFullTeaserTileBlockById :
      getTeaserTileBlockById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'teaserTileBlock',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],
    queryFn: async () => {
      const data = await contentfulService.query(
        print(query),
        {id, locale: contentfulService.locale, ...variables},
        requestOptions
      );
      return data?.teaserTileBlock ?? null;
    },
    enabled: Boolean(contentfulService && id),
    ...queryOptions,
  });
}
